import { log, logger } from '@/helpers';
import { CampaignReportFilters } from '@/types';
import { create, StoreApi, UseBoundStore } from 'zustand';
import { useCampaignReportCampaignStore } from '../campaign-report/useCampaignReportCampaignStore.ts';
import { getCoMarketingReportChannelPerformanceStore } from '../co-marketing-report/createCoMarketingReportChannelPerformanceStore.ts';
import { useCampaignReportCampaignListStore } from './createCampaignReportCampaignListStore.ts';
import { getCampaignReportChannelPerformanceStore } from './createCampaignReportChannelPerformanceStore.ts';
import { getCampaignReportConversionReportStore } from './createCampaignReportConversionReportStore.ts';
import { getCampaignReportTopConvertingStore } from './createCampaignReportTopConvertingStore.ts';

export type RawCampaignReportFilters = {
	strictAttribution: 'true' | 'false';
	campaignIDs: string[];
	campaignTags: string[];
	campaignStatus: string[];
	startDate: string | undefined;
	endDate: string | undefined;
	variantIDs: string[];
};

export type ReportingFiltersStoreState = {
	campaignID: string;
	templateID?: number;
	retailerCampaignID?: string;
	rawFilters: RawCampaignReportFilters;
};

export type ReportingFiltersStoreActions = {
	initialize: () => void;
	getReportFiltersFromRawFilters: (campaignID?: string, variantID?: string) => CampaignReportFilters;
	setRawFilters: (filters: RawCampaignReportFilters, initial?: boolean) => void;
	resetFilters: () => void;
};

export type ReportingFiltersStore = ReportingFiltersStoreState & ReportingFiltersStoreActions;

const defaultFilters = (): RawCampaignReportFilters => {
	// const dates = dateRangeFilterPresets['last-3-months'].apply();
	return {
		// startDate: dates.startDate.toISOString(),
		// endDate: dates.endDate.toISOString(),
		startDate: undefined,
		endDate: undefined,
		strictAttribution: 'true',
		campaignIDs: [],
		campaignTags: [],
		campaignStatus: [],
		variantIDs: [],
	};
};

const initialState: ReportingFiltersStoreState = {
	campaignID: 'global',
	rawFilters: defaultFilters(),
};

export const createCampaignReportFiltersStore = (campaignID = 'global', templateID?: number, retailerCampaignID?: string) =>
	create<ReportingFiltersStore>((set, get) => ({
		...initialState,
		campaignID,
		retailerCampaignID,
		templateID,
		initialize: () => {
			const queryFilters = getRawFiltersFromQuery();
			get().setRawFilters(queryFilters, true);
		},
		getReportFiltersFromRawFilters: (campaignID = get().campaignID, variantID = '') => {
			return getReportFiltersFromRawFilters(get().rawFilters, campaignID, variantID);
		},
		setRawFilters: (rawFilters: RawCampaignReportFilters, initial = false) => {
			set({ rawFilters });
			if (!initial) setRawFiltersInQuery(rawFilters);

			const filters = getReportFiltersFromRawFilters(rawFilters, campaignID);
			logger.log('setRawFilters').prefix(campaignID).addnl('filters', filters);

			// When filters change if they are global we reset the report campaign list store
			if (get().campaignID === 'global') {
				useCampaignReportCampaignListStore.getState().reset(filters);
			}

			// IS co-marketing if url contains ?templateID param
			const isCoMarketing = window.location.search.includes('templateID');

			// If it's an A/B variant campaign, we load data for each variant but not for the total campaign
			if (campaignID && campaignID !== 'global' && !isCoMarketing) {
				const campaign = useCampaignReportCampaignStore.getState().campaign;
				// If it's an a/b campaign, we must load all the variants
				if (campaign?.abVariants?.length) {
					for (const variant of campaign?.abVariants ?? []) {
						// Reset the channel performance store for each variant
						const filters = getReportFiltersFromRawFilters(rawFilters, campaignID, variant.id);
						getCampaignReportTopConvertingStore(campaignID, variant.id).getState().reset(filters);
						getCampaignReportChannelPerformanceStore(campaignID, variant.id).getState().reset(filters);
						getCampaignReportConversionReportStore(campaignID, variant.id).getState().reset(filters);
					}
					return;
				}
			}

			if (isCoMarketing) {
				getCoMarketingReportChannelPerformanceStore(campaignID, get().retailerCampaignID).getState().reset(filters);
			} else {
				getCampaignReportChannelPerformanceStore(campaignID).getState().reset(filters);
			}
			getCampaignReportTopConvertingStore(campaignID, undefined, get().retailerCampaignID).getState().reset(filters);
			getCampaignReportConversionReportStore(campaignID, undefined, get().retailerCampaignID).getState().reset(filters);
		},
		resetFilters: () => set(initialState),
	}));

const storeInstances = new Map<string, UseBoundStore<StoreApi<ReportingFiltersStore>>>();

export const getCampaignReportFiltersStore = (id = 'global', templateID?: number, retailerCampaignID?: string) => {
	const key = `${id}-${templateID}-${retailerCampaignID}`;
	if (!storeInstances.has(key)) {
		storeInstances.set(key, createCampaignReportFiltersStore(id, templateID, retailerCampaignID));
	}
	return storeInstances.get(key)!;
};

function getRawFiltersFromQuery(): RawCampaignReportFilters {
	const url = new URL(window.location.href);
	const searchParams = url.searchParams;

	const filters: RawCampaignReportFilters = defaultFilters();

	if (searchParams.has('strictAttribution')) {
		filters.strictAttribution = searchParams.get('strictAttribution') as 'true' | 'false';
	}
	if (searchParams.has('campaignIDs')) {
		filters.campaignIDs = searchParams.get('campaignIDs')!.split(',').filter(Boolean);
	}
	if (searchParams.has('campaignTags')) {
		filters.campaignTags = searchParams.get('campaignTags')!.split(',').filter(Boolean);
	}
	if (searchParams.has('campaignStatus')) {
		filters.campaignStatus = searchParams.get('campaignStatus')!.split(',').filter(Boolean);
	}
	if (searchParams.has('startDate')) {
		filters.startDate = searchParams.get('startDate')!;
	}
	if (searchParams.has('endDate')) {
		filters.endDate = searchParams.get('endDate')!;
	}

	return filters;
}

function setRawFiltersInQuery(rawFilters: Partial<RawCampaignReportFilters>) {
	const url = new URL(window.location.href);
	const searchParams = url.searchParams;

	const setOrDelete = (key: string, value: string, defaultValue: string) => {
		if (value !== defaultValue && value !== '' && value !== '0' && value !== 'false') {
			searchParams.set(key, value);
		} else {
			searchParams.delete(key);
		}
	};

	setOrDelete('strictAttribution', String(rawFilters.strictAttribution ?? true), String(true));
	setOrDelete('campaignIDs', rawFilters.campaignIDs?.join(',') ?? '', '');
	setOrDelete('campaignTags', rawFilters.campaignTags?.join(',') ?? '', '');
	setOrDelete('campaignStatus', rawFilters.campaignStatus?.join(',') ?? '', '');
	setOrDelete('startDate', rawFilters.startDate ?? '', '');
	setOrDelete('endDate', rawFilters.endDate ?? '', '');

	window.history.pushState({}, '', url.toString());
}

function getReportFiltersFromRawFilters(rawFilters: RawCampaignReportFilters, campaignID?: string, variantID?: string): CampaignReportFilters {
	const isGlobal = campaignID === 'global';

	const filters: CampaignReportFilters = {
		strictAttribution: rawFilters.strictAttribution !== 'false',
		campaignIDs: campaignID && !isGlobal ? [campaignID] : rawFilters.campaignIDs,
		campaignTags: rawFilters.campaignTags,
		campaignStatus: {
			active: rawFilters.campaignStatus?.includes('active'),
			inactive: rawFilters.campaignStatus?.includes('inactive'),
			archived: rawFilters.campaignStatus?.includes('archived'),
		},
		startDate: rawFilters.startDate ? Math.floor(new Date(rawFilters.startDate).getTime() / 1000) : 0,
		endDate: rawFilters.endDate ? Math.floor(new Date(rawFilters.endDate).getTime() / 1000) : 0,
		variantIDs: variantID ? [variantID] : rawFilters.variantIDs,
	};

	return filters;
}

// To test prod endpoint hits locally, set these values
// To obtain info: In console (on prod), run `aauth()`
// Then copy the token and paste it below along with the uid
export const LOCAL_CAMPAIGN_REPORT_TEST_TOKEN = '';
export const LOCAL_CAMPAIGN_REPORT_TEST_UID = '';
